/**
 * The single list of primary navigation destinations.
 * `/new-project` edits only this file to change an app's navigation.
 */
import {
  Activity,
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardCheck,
  FileText,
  Home,
  type LucideIcon,
  MessageSquare,
  Package,
  Settings,
  Users,
  UsersRound,
} from 'lucide-react'
import type { Permission } from '@/core/access'

export interface NavItem {
  id: string
  title: string
  icon: LucideIcon
  url: string
  /** Eligible for a count badge when the caller supplies one. */
  badge?: boolean
  /**
   * A section heading this item sits under in the sidebar (e.g. "Raw Data").
   * Items without one come first, with no heading. Items sharing a group are
   * drawn together, in the order the group first appears.
   */
  group?: string
}

export interface NavSection {
  /** The heading, or null for the ungrouped items at the top. */
  group: string | null
  items: NavItem[]
}

/** Split nav items into sidebar sections: ungrouped first, then each group in first-seen order. */
export function navSections(items: readonly NavItem[]): NavSection[] {
  const ungrouped: NavItem[] = []
  const groups = new Map<string, NavItem[]>()
  for (const item of items) {
    if (!item.group) {
      ungrouped.push(item)
      continue
    }
    const list = groups.get(item.group) ?? []
    list.push(item)
    groups.set(item.group, list)
  }
  const sections: NavSection[] = ungrouped.length ? [{ group: null, items: ungrouped }] : []
  for (const [group, list] of groups) sections.push({ group, items: list })
  return sections
}

/**
 * Majik's sections, a flat list as in the approved prototype
 * (design/prototype/Sidebar.dc.html). The phone's tab bar shows the first four
 * and puts the rest under More, so the order is Gideon's pick for the phone
 * (Oct 24): Today, Jobs, Clients, Messages first.
 */
export const navItems: NavItem[] = [
  { id: 'home', title: 'Today', icon: Home, url: '/' },
  { id: 'jobs', title: 'Jobs', icon: Briefcase, url: '/jobs' },
  { id: 'clients', title: 'Clients', icon: Building2, url: '/clients' },
  { id: 'messages', title: 'Messages', icon: MessageSquare, url: '/messages' },
  { id: 'schedule', title: 'Schedule', icon: CalendarDays, url: '/schedule' },
  { id: 'cleaners', title: 'Cleaners', icon: UsersRound, url: '/cleaners' },
  { id: 'inspections', title: 'Inspections', icon: ClipboardCheck, url: '/inspections' },
  { id: 'invoices', title: 'Invoices', icon: FileText, url: '/invoices' },
  { id: 'orders', title: 'Orders', icon: Package, url: '/orders' },
  // Its own page since Oct 26 (Gideon): the products and their stock.
  { id: 'inventory', title: 'Inventory', icon: Boxes, url: '/inventory' },
]

/** A nav item is active on its own route and anything nested under it. */
export function isNavItemActive(pathname: string, url: string): boolean {
  if (url === '/') return pathname === '/'
  return pathname === url || pathname.startsWith(`${url}/`)
}

/** Screens whose address doesn't read as a title ("mcp" is an acronym, not a word). */
const TITLE_OVERRIDES: Record<string, string> = {
  mcp: 'MCP access',
  import: 'Import from Monday',
}

/**
 * The admin destinations, all under /settings, with the permission each needs.
 * Not in navItems (the app's own navigation, rewritten per project): every app
 * keeps these. The sidebar footer and the phone's More sheet both read this list.
 */
export interface AdminNavItem {
  id: string
  title: string
  icon: LucideIcon
  url: string
  permission: Permission
  /** Paths that count as this destination for the active state. */
  isActive: (pathname: string) => boolean
}

export const adminNavItems: AdminNavItem[] = [
  {
    id: 'system',
    title: 'System',
    icon: Activity,
    url: '/settings/system',
    permission: 'system.view',
    isActive: (p) => isNavItemActive(p, '/settings/system'),
  },
  {
    id: 'users',
    title: 'Users',
    icon: Users,
    url: '/settings/users',
    permission: 'users.manage',
    isActive: (p) => isNavItemActive(p, '/settings/users'),
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: Settings,
    url: '/settings',
    permission: 'settings.manage',
    isActive: (p) =>
      isNavItemActive(p, '/settings') &&
      !['/settings/users', '/settings/system'].some((url) => isNavItemActive(p, url)),
  },
]

/** "/settings/account" → "Account"; "/kitchen-sink" → "Kitchen sink"; "/" → "Home". */
export function titleFromPathname(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean).pop()
  if (!segment) return 'Home'
  const override = TITLE_OVERRIDES[segment]
  if (override) return override
  const words = segment.replace(/-/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
